import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';

const UpdatePassword: React.FC = () => {
  const [formData, setFormData] = useState({
    user: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validações
    if (!formData.user.trim() || !formData.oldPassword.trim() || !formData.newPassword.trim() || !formData.confirmPassword.trim()) {
      setError('All fields are required');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (formData.newPassword === formData.oldPassword) {
      setError('New password must be different from current password');
      return;
    }

    // Validação de email da Iterpec
    if (formData.user.includes('@') && !formData.user.toLowerCase().endsWith('@iterpec.com')) {
      setError('Only Iterpec employees can access this system. Please use your @iterpec.com email.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await authService.updatePassword({
        user: formData.user,
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword
      });

      // Redirecionar para login após sucesso
      navigate('/login', { 
        state: { message: 'Password updated successfully. Please login again.' }
      });
      
    } catch (err: any) {
      console.error('Update password error:', err);
      
      if (err.response?.status === 400) {
        setError(err.response?.data?.message || 'Failed to update password. Please check your current password.');
      } else if (err.response?.status === 401) {
        setError('Invalid current password');
      } else if (err.response?.status === 403) {
        setError('Access denied. Please contact administrator.');
      } else {
        setError('Failed to update password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro quando usuário começar a digitar
    if (error) {
      setError(null);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <h2 className="fw-bold text-dark">Update Password</h2>
                  <p className="text-muted">Please set a new password for your account</p>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="user" className="form-label">
                      <i className="bi bi-person me-1"></i>
                      Username/Email
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="user"
                      name="user"
                      value={formData.user}
                      onChange={handleInputChange}
                      placeholder="Enter your username or email"
                      required
                      disabled={loading}
                      autoComplete="username"
                    />
                    <div className="form-text">
                      Use your @iterpec.com email address
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="oldPassword" className="form-label">
                      <i className="bi bi-lock me-1"></i>
                      Current Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="oldPassword"
                      name="oldPassword"
                      value={formData.oldPassword}
                      onChange={handleInputChange}
                      placeholder="Enter your current password"
                      required
                      disabled={loading}
                      autoComplete="current-password"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="newPassword" className="form-label">
                      <i className="bi bi-shield-lock me-1"></i>
                      New Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="newPassword"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      placeholder="Enter your new password"
                      required
                      disabled={loading}
                      minLength={8}
                      autoComplete="new-password"
                    />
                    <div className="form-text">
                      Password must be at least 8 characters long
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="confirmPassword" className="form-label">
                      <i className="bi bi-shield-check me-1"></i>
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm your new password"
                      required
                      disabled={loading}
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary w-100"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Updating Password...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-shield-check me-2"></i>
                        Update Password
                      </>
                    )}
                  </button>
                </form>

                <div className="text-center mt-4">
                  <a href="/login" className="text-decoration-none">
                    <i className="bi bi-arrow-left me-1"></i>
                    Back to Login
                  </a>
                </div>

                <div className="mt-4">
                  <div className="alert alert-info" role="alert">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Password Requirements:</strong>
                    <ul className="mb-0 mt-2">
                      <li>At least 8 characters long</li>
                      <li>Must be different from current password</li>
                      <li>Use your @iterpec.com email address</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;
